import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const AdminPermissionsPage = () => {
  const { hasPermission } = useAuth();
  const [userTypes, setUserTypes] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [selectedUserTypePermissions, setSelectedUserTypePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Modals
  const [showCreateUserTypeModal, setShowCreateUserTypeModal] = useState(false);
  const [showEditUserTypeModal, setShowEditUserTypeModal] = useState(false);
  const [showDeleteUserTypeModal, setShowDeleteUserTypeModal] = useState(false);
  const [showCloneUserTypeModal, setShowCloneUserTypeModal] = useState(false);
  const [showCreatePermissionModal, setShowCreatePermissionModal] = useState(false);
  const [showEditPermissionModal, setShowEditPermissionModal] = useState(false);
  const [showDeletePermissionModal, setShowDeletePermissionModal] = useState(false);
  const [showAssignPermissionsModal, setShowAssignPermissionsModal] = useState(false);
  
  // Form data
  const [userTypeFormData, setUserTypeFormData] = useState({ type_name: '', description: '' });
  const [permissionFormData, setPermissionFormData] = useState({ 
    permission_name: '', 
    description: '', 
    resource_type: '', 
    action_type: '' 
  });
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToClone, setItemToClone] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const fetchUserTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/user-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserTypes(response.data.user_types || []);
    } catch (err) {
      console.error('Error fetching user types:', err);
      setError('Failed to fetch user types');
    }
  }, [API_BASE_URL]);

  const fetchPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPermissions(response.data.permissions || []);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Failed to fetch permissions');
    }
  }, [API_BASE_URL]);

  const fetchUserTypePermissions = useCallback(async (userTypeId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/user-types/${userTypeId}/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedUserTypePermissions(response.data.permissions || []);
    } catch (err) {
      console.error('Error fetching user type permissions:', err);
      setError('Failed to fetch user type permissions');
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    if (hasPermission('system.admin')) {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([fetchUserTypes(), fetchPermissions()]);
        setLoading(false);
      };
      loadData();
    }
  }, [hasPermission, fetchUserTypes, fetchPermissions]);

  useEffect(() => {
    if (selectedUserType) {
      fetchUserTypePermissions(selectedUserType.user_type_id);
    } else {
      setSelectedUserTypePermissions([]);
    }
  }, [selectedUserType, fetchUserTypePermissions]);

  const handleUserTypeClick = (userType) => {
    setSelectedUserType(userType);
  };

  const handleCreateUserType = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API_BASE_URL}/admin/user-types`, userTypeFormData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('User type created successfully');
      setShowCreateUserTypeModal(false);
      setUserTypeFormData({ type_name: '', description: '' });
      await fetchUserTypes();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user type');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleEditUserType = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE_URL}/admin/user-types/${itemToEdit.user_type_id}`, userTypeFormData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('User type updated successfully');
      setShowEditUserTypeModal(false);
      setUserTypeFormData({ type_name: '', description: '' });
      setItemToEdit(null);
      await fetchUserTypes();
      if (selectedUserType?.user_type_id === itemToEdit.user_type_id) {
        setSelectedUserType(null);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user type');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeleteUserType = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_BASE_URL}/admin/user-types/${itemToDelete.user_type_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('User type deleted successfully');
      setShowDeleteUserTypeModal(false);
      setItemToDelete(null);
      await fetchUserTypes();
      if (selectedUserType?.user_type_id === itemToDelete.user_type_id) {
        setSelectedUserType(null);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user type');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleCreatePermission = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API_BASE_URL}/admin/permissions`, permissionFormData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('Permission created successfully');
      setShowCreatePermissionModal(false);
      setPermissionFormData({ permission_name: '', description: '', resource_type: '', action_type: '' });
      await fetchPermissions();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create permission');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleEditPermission = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE_URL}/admin/permissions/${itemToEdit.permission_id}`, permissionFormData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('Permission updated successfully');
      setShowEditPermissionModal(false);
      setPermissionFormData({ permission_name: '', description: '', resource_type: '', action_type: '' });
      setItemToEdit(null);
      await fetchPermissions();
      if (selectedUserType) {
        await fetchUserTypePermissions(selectedUserType.user_type_id);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update permission');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeletePermission = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_BASE_URL}/admin/permissions/${itemToDelete.permission_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('Permission deleted successfully');
      setShowDeletePermissionModal(false);
      setItemToDelete(null);
      await fetchPermissions();
      if (selectedUserType) {
        await fetchUserTypePermissions(selectedUserType.user_type_id);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete permission');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleAssignPermissions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE_URL}/admin/user-types/${selectedUserType.user_type_id}/permissions`, 
        { permission_ids: selectedPermissionIds },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSuccessMessage('Permissions assigned successfully');
      setShowAssignPermissionsModal(false);
      setSelectedPermissionIds([]);
      await fetchUserTypePermissions(selectedUserType.user_type_id);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign permissions');
      setTimeout(() => setError(null), 5000);
    }
  };

  const openEditUserTypeModal = (userType) => {
    setItemToEdit(userType);
    setUserTypeFormData({ type_name: userType.type_name, description: userType.description });
    setShowEditUserTypeModal(true);
  };

  const openDeleteUserTypeModal = (userType) => {
    setItemToDelete(userType);
    setShowDeleteUserTypeModal(true);
  };

  const openCloneUserTypeModal = (userType) => {
    setItemToClone(userType);
    setUserTypeFormData({ type_name: '', description: '' });
    setShowCloneUserTypeModal(true);
  };

  const handleCloneUserType = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${API_BASE_URL}/admin/user-types/${itemToClone.user_type_id}/clone`, 
        { 
          type_name: userTypeFormData.type_name,
          description: userTypeFormData.description 
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSuccessMessage('User type cloned successfully');
      setShowCloneUserTypeModal(false);
      setUserTypeFormData({ type_name: '', description: '' });
      setItemToClone(null);
      await fetchUserTypes();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clone user type');
      setTimeout(() => setError(null), 5000);
    }
  };

  const openEditPermissionModal = (permission) => {
    setItemToEdit(permission);
    setPermissionFormData({
      permission_name: permission.permission_name,
      description: permission.description,
      resource_type: permission.resource_type,
      action_type: permission.action_type
    });
    setShowEditPermissionModal(true);
  };

  const openDeletePermissionModal = (permission) => {
    setItemToDelete(permission);
    setShowDeletePermissionModal(true);
  };

  const openAssignPermissionsModal = () => {
    const currentPermissionIds = selectedUserTypePermissions.map(p => p.permission_id);
    setSelectedPermissionIds(currentPermissionIds);
    setShowAssignPermissionsModal(true);
  };

  const togglePermissionSelection = (permissionId) => {
    setSelectedPermissionIds(prev => 
      prev.includes(permissionId) 
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  if (!hasPermission('system.admin')) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-12 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6">
      <div className="w-full mx-auto flex-shrink-0">
        <h1 className="text-2xl font-bold mb-4">Permissions Management</h1>
        
        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {successMessage}
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
          {/* User Types Panel */}
          <div className="bg-white rounded-lg shadow p-4 flex flex-col min-h-0">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold">User Types</h2>
              <button
                onClick={() => setShowCreateUserTypeModal(true)}
                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
              >
                + New
              </button>
            </div>
            
            <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
              {userTypes.map((userType) => (
                <div
                  key={userType.user_type_id}
                  className={`p-3 border rounded cursor-pointer hover:bg-gray-50 ${
                    selectedUserType?.user_type_id === userType.user_type_id ? 'bg-blue-50 border-blue-500' : ''
                  }`}
                  onClick={() => handleUserTypeClick(userType)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold">{userType.type_name}</div>
                      <div className="text-sm text-gray-600">{userType.description}</div>
                    </div>
                    <div className="flex space-x-1 ml-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditUserTypeModal(userType); }}
                        className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openCloneUserTypeModal(userType); }}
                        className="px-2 py-1 text-xs bg-blue-200 text-blue-700 rounded hover:bg-blue-300"
                      >
                        Clone
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openDeleteUserTypeModal(userType); }}
                        className="px-2 py-1 text-xs bg-red-200 text-red-700 rounded hover:bg-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Assigned Permissions Panel */}
          <div className="bg-white rounded-lg shadow p-4 flex flex-col min-h-0">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold">
                {selectedUserType ? `${selectedUserType.type_name} Permissions` : 'Select a User Type'}
              </h2>
              {selectedUserType && (
                <button
                  onClick={openAssignPermissionsModal}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                >
                  Assign
                </button>
              )}
            </div>
            
            {selectedUserType ? (
              <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                {selectedUserTypePermissions.length > 0 ? (
                  selectedUserTypePermissions.map((permission) => (
                    <div key={permission.permission_id} className="p-3 border rounded">
                      <div className="font-semibold">{permission.permission_name}</div>
                      <div className="text-sm text-gray-600">{permission.description}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {permission.resource_type} / {permission.action_type}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    No permissions assigned to this user type
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                Click on a user type to view its permissions
              </div>
            )}
          </div>

          {/* All Permissions Panel */}
          <div className="bg-white rounded-lg shadow p-4 flex flex-col min-h-0">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold">All Permissions</h2>
              <button
                onClick={() => setShowCreatePermissionModal(true)}
                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
              >
                + New
              </button>
            </div>
            
            <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
              {permissions.map((permission) => (
                <div key={permission.permission_id} className="p-3 border rounded">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold">{permission.permission_name}</div>
                      <div className="text-sm text-gray-600">{permission.description}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {permission.resource_type} / {permission.action_type}
                      </div>
                    </div>
                    <div className="flex space-x-1 ml-2">
                      <button
                        onClick={() => openEditPermissionModal(permission)}
                        className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openDeletePermissionModal(permission)}
                        className="px-2 py-1 text-xs bg-red-200 text-red-700 rounded hover:bg-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Create User Type Modal */}
      {showCreateUserTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Create User Type</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type Name *</label>
                <input
                  type="text"
                  value={userTypeFormData.type_name}
                  onChange={(e) => setUserTypeFormData({...userTypeFormData, type_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., editor"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={userTypeFormData.description}
                  onChange={(e) => setUserTypeFormData({...userTypeFormData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="3"
                  placeholder="Description of this user type"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => { setShowCreateUserTypeModal(false); setUserTypeFormData({ type_name: '', description: '' }); }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUserType}
                disabled={!userTypeFormData.type_name || !userTypeFormData.description}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Type Modal */}
      {showEditUserTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit User Type</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type Name *</label>
                <input
                  type="text"
                  value={userTypeFormData.type_name}
                  onChange={(e) => setUserTypeFormData({...userTypeFormData, type_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={userTypeFormData.description}
                  onChange={(e) => setUserTypeFormData({...userTypeFormData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="3"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => { setShowEditUserTypeModal(false); setUserTypeFormData({ type_name: '', description: '' }); setItemToEdit(null); }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUserType}
                disabled={!userTypeFormData.type_name || !userTypeFormData.description}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone User Type Modal */}
      {showCloneUserTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Clone User Type</h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Create a copy of "{itemToClone?.type_name}" with the same permissions but with a new name.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Type Name *</label>
                <input
                  type="text"
                  value={userTypeFormData.type_name}
                  onChange={(e) => setUserTypeFormData({...userTypeFormData, type_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., editor_advanced"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                <textarea
                  value={userTypeFormData.description}
                  onChange={(e) => setUserTypeFormData({...userTypeFormData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="3"
                  placeholder={`Clone of ${itemToClone?.type_name}`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use default description
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => { 
                  setShowCloneUserTypeModal(false); 
                  setUserTypeFormData({ type_name: '', description: '' }); 
                  setItemToClone(null); 
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCloneUserType}
                disabled={!userTypeFormData.type_name}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Clone
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Type Modal */}
      {showDeleteUserTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete User Type</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the user type "{itemToDelete?.type_name}"?
              <br /><br />
              <span className="text-red-600 font-semibold">This will remove all permission assignments for this user type.</span>
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowDeleteUserTypeModal(false); setItemToDelete(null); }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUserType}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Permission Modal */}
      {showCreatePermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Create Permission</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permission Name *</label>
                <input
                  type="text"
                  value={permissionFormData.permission_name}
                  onChange={(e) => setPermissionFormData({...permissionFormData, permission_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., content.edit"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={permissionFormData.description}
                  onChange={(e) => setPermissionFormData({...permissionFormData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="2"
                  placeholder="Description of this permission"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type *</label>
                <input
                  type="text"
                  value={permissionFormData.resource_type}
                  onChange={(e) => setPermissionFormData({...permissionFormData, resource_type: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., content, user, system"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Type *</label>
                <input
                  type="text"
                  value={permissionFormData.action_type}
                  onChange={(e) => setPermissionFormData({...permissionFormData, action_type: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., read, write, delete, admin"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => { 
                  setShowCreatePermissionModal(false); 
                  setPermissionFormData({ permission_name: '', description: '', resource_type: '', action_type: '' }); 
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePermission}
                disabled={!permissionFormData.permission_name || !permissionFormData.description || 
                          !permissionFormData.resource_type || !permissionFormData.action_type}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Permission Modal */}
      {showEditPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Permission</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permission Name *</label>
                <input
                  type="text"
                  value={permissionFormData.permission_name}
                  onChange={(e) => setPermissionFormData({...permissionFormData, permission_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={permissionFormData.description}
                  onChange={(e) => setPermissionFormData({...permissionFormData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type *</label>
                <input
                  type="text"
                  value={permissionFormData.resource_type}
                  onChange={(e) => setPermissionFormData({...permissionFormData, resource_type: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Type *</label>
                <input
                  type="text"
                  value={permissionFormData.action_type}
                  onChange={(e) => setPermissionFormData({...permissionFormData, action_type: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => { 
                  setShowEditPermissionModal(false); 
                  setPermissionFormData({ permission_name: '', description: '', resource_type: '', action_type: '' }); 
                  setItemToEdit(null); 
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditPermission}
                disabled={!permissionFormData.permission_name || !permissionFormData.description || 
                          !permissionFormData.resource_type || !permissionFormData.action_type}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Permission Modal */}
      {showDeletePermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Permission</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the permission "{itemToDelete?.permission_name}"?
              <br /><br />
              <span className="text-red-600 font-semibold">This will remove this permission from all user types.</span>
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowDeletePermissionModal(false); setItemToDelete(null); }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePermission}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Permissions Modal */}
      {showAssignPermissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Assign Permissions to {selectedUserType?.type_name}
            </h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Select the permissions you want to assign to this user type. Selected permissions are checked.
            </p>
            
            <div className="space-y-2 mb-6">
              {permissions.map((permission) => (
                <div key={permission.permission_id} className="flex items-start p-3 border rounded hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedPermissionIds.includes(permission.permission_id)}
                    onChange={() => togglePermissionSelection(permission.permission_id)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-semibold">{permission.permission_name}</div>
                    <div className="text-sm text-gray-600">{permission.description}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {permission.resource_type} / {permission.action_type}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowAssignPermissionsModal(false); setSelectedPermissionIds([]); }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignPermissions}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPermissionsPage;
